import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import {
  Button,
  Input,
  Select,
  SiloTypeSelect,
  checkEmptyRequiredFields,
} from '@web/utils';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import { useUserContext } from '@/provider';
import { Address, Silo, SiloUpdateInput } from '@/types';
import {
  useCreateSiloMutation,
  useUpdateSiloMutation,
} from '../Silos.generated';

const siloInputFields = [
  {
    id: 'name',
    name: 'name',
    required: true,
  },
  {
    id: 'addressID',
    name: 'address',
    required: true,
  },
  {
    id: 'capacity',
    name: 'capacity',
    type: 'number',
    required: true,
  },
  { id: 'description', name: 'description', required: true },
  {
    id: 'icon',
    name: 'icon',
  },
];

export enum SiloTypes {
  FLAT = 'FLAT',
  HOPPER = 'HOPPER',
}

const SiloFormDefaultValues: SiloUpdateInput = {
  addressID: '',
  name: '',
  capacity: 0,
  currentProduce: '',
  freeCapacity: 0,
  description: '',
  icon: SiloTypes.FLAT,
};

export interface CreateOrEditSiloFormProps {
  setIsOpen: (isOpen: boolean) => void;
  isEdit?: boolean;
  silo?: Silo;
}

const CreateOrEditSiloForm = (props: CreateOrEditSiloFormProps) => {
  const { isEdit = false, silo, setIsOpen } = props;
  const { t } = useTranslation();
  const toast = useCustomToast();
  const { user } = useUserContext();
  const [{ fetching }, createSiloMutation] = useCreateSiloMutation();
  const [_, updateSiloMutation] = useUpdateSiloMutation();
  const [siloForm, setSiloForm] = useState<SiloUpdateInput>(
    SiloFormDefaultValues,
  );

  useEffect(() => {
    if (silo && isEdit) {
      const newSiloForm = {
        addressID: silo.addressID,
        name: silo.name,
        capacity: silo.capacity,
        currentProduce: silo.currentProduce,
        freeCapacity: silo.freeCapacity,
        description: silo.description,
        icon: silo.icon || SiloTypes.FLAT,
      };

      // Only update if the new form data is different from the current state
      if (JSON.stringify(newSiloForm) !== JSON.stringify(siloForm)) {
        setSiloForm(newSiloForm);
      }
    } else if (user && user.addresses.length > 0) {
      if (user.addresses[0].id !== siloForm.addressID) {
        setSiloForm((prev) => ({
          ...prev,
          addressID: user.addresses[0].id || '1',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo, isEdit, user]);

  const addressLine = (address: Omit<Address, 'owner' | 'ownerId'>) => {
    return `${address.addressLine1}, ${address.addressLine2 || ''} ${
      address.addressLocality
    }, ${address.addressPostcode}`;
  };

  const handleInputChange = (name, value) => {
    setSiloForm({ ...siloForm, [name]: value });
  };

  const handleCancelButton = () => {
    setSiloForm(SiloFormDefaultValues);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    // check if any of the form fields that are required are empty
    const areAnyRequiredFieldsEmpty = checkEmptyRequiredFields(
      siloInputFields,
      siloForm,
    );

    if (areAnyRequiredFieldsEmpty.length > 0) {
      toast('error', t('farm.silos.fill_required_fields'));
      return;
    }

    if (siloForm.capacity < siloForm.freeCapacity) {
      toast('error', t('farm.silos.capacity_error'));
      return;
    }
    if (!isEdit) {
      try {
        const result = await createSiloMutation({
          silo: { ...siloForm, freeCapacity: siloForm.capacity },
        });
        if (result.data?.createSilo) {
          setSiloForm(SiloFormDefaultValues);
          toast('success', t('farm.silos.created_sucessfully'));
        } else {
          toast('error', t('farm.silos.error'));
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      try {
        const result = await updateSiloMutation({
          silo: { ...siloForm, freeCapacity: siloForm.capacity },
          id: silo?.id || '1',
        });
        if (result.data?.updateSilo) {
          setSiloForm(SiloFormDefaultValues);
          toast('success', t('farm.silos.updated_sucessfully'));
        } else {
          toast('error', t('farm.silos.error'));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <div className="bg-white rounded-8-0 p-2-0">
      <div className="flex justify-between">
        <p className="text-2-0 font-bd mb-2-0">
          {t('farm.silos.add_new_silo')}
        </p>
        <div
          className="rotate-45 cursor-pointer opacity-50 mr-1-0 "
          data-testid="close"
          onClick={handleCancelButton}
        >
          <ReactSVG src={plusIcon} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-y-2-0">
        <Select
          id="addressID"
          options={user.addresses.map((address: Address) => ({
            name: addressLine(address),
            id: address.id,
          }))}
          label={t('farm.silos.address') ?? ''}
          onChange={handleInputChange}
          selected={siloForm.addressID ?? undefined}
          data-testid="addressID"
          required
        />
        {siloInputFields
          .filter((i) => !['addressID', 'icon'].includes(i.id))
          .map((item) => (
            <Input
              key={item.id}
              onChange={handleInputChange}
              label={t(`farm.silos.${item.name}` as any) ?? ''}
              id={item.id}
              type={item.type}
              step={item.type === 'number' ? 0.001 : undefined}
              required={item.required}
              disabled={fetching}
              value={siloForm[item.id]}
              data-testid={item.id}
            />
          ))}
        <SiloTypeSelect
          type={siloForm.icon || SiloTypes.FLAT}
          t={t}
          handleInputChange={handleInputChange}
        />
      </div>
      <div className="mt-1-0">
        <Button onClick={handleSubmit} data-testid="submit-btn">
          {t('global.submit')}
        </Button>
      </div>
    </div>
  );
};
export default CreateOrEditSiloForm;
